import { isJust } from "../../lib/maybe";
/**
 * Scrolling for the ProseMirror editor (#249): caret-follow and typewriter
 * mode on the shell's outer scroller (`.zw-editor-scroll`), sharing the
 * geometry helpers and keyboard / dock insets in `../scrollIntoView` and
 * `../typewriterScroll`.
 */

import type { Node as PMNode } from "prosemirror-model";
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import {
  applyScrollDelta,
  computeScrollDelta,
  scrollBottomChromePx,
  visibleScrollerRect,
  type ScrollIntoViewOptions,
} from "../scrollIntoView";
import { dimOpacityForDistance } from "../typewriterScroll";

export type ScrollHost = () => HTMLElement | null;

/** Scroll `host` so the caret at `pos` is in view per `opts`. */
export function scrollPosIntoHost(
  view: EditorView,
  host: HTMLElement,
  pos: number,
  opts: ScrollIntoViewOptions = {},
): void {
  const clamped = Math.max(0, Math.min(pos, view.state.doc.content.size));
  const coords = view.coordsAtPos(clamped);
  const delta = computeScrollDelta(
    { top: coords.top, bottom: coords.bottom },
    visibleScrollerRect(
      host.getBoundingClientRect(),
      scrollBottomChromePx(host),
    ),
    opts,
  );
  if (isJust(delta))
    applyScrollDelta(host, delta.value, opts.behavior ?? "auto");
}

// --- Typewriter ---------------------------------------------------------------

type TypewriterState = {
  active: boolean;
  /** A nav scroll (outline / search jump) owns the position until the next edit. */
  yielded: boolean;
  decorations: DecorationSet;
};

type TypewriterMeta = { active: boolean } | { yield: true };

const typewriterKey = new PluginKey<TypewriterState>("zwTypewriter");

/** Dim top-level blocks by distance from the one holding the caret. */
function dimDecorations(state: EditorState): DecorationSet {
  const { doc, selection } = state;
  const active = selection.$head.index(0);
  const decorations: Decoration[] = [];
  doc.forEach((block: PMNode, offset: number, index: number) => {
    const opacity = dimOpacityForDistance(Math.abs(index - active));
    if (opacity >= 1) return;
    decorations.push(
      Decoration.node(offset, offset + block.nodeSize, {
        class: "zw-typewriter-dim",
        style: `opacity: ${opacity}`,
      }),
    );
  });
  return DecorationSet.create(doc, decorations);
}

function recenter(view: EditorView, host: HTMLElement): void {
  const coords = view.coordsAtPos(view.state.selection.head);
  const rect = host.getBoundingClientRect();
  const visibleHeight = Math.max(0, rect.height - scrollBottomChromePx(host));
  const delta = coords.top - (rect.top + visibleHeight / 2);
  if (Math.abs(delta) >= 1) host.scrollTop += delta;
}

/**
 * Typewriter mode: the caret stays at the vertical centre of the visible
 * scroller and other paragraphs dim. Off until `setTypewriter(view, true)`.
 * Pauses while a range is selected or the mouse is dragging, and after
 * `yieldTypewriter` until the next edit.
 */
export function typewriterPlugin(
  scrollHost: ScrollHost,
): Plugin<TypewriterState> {
  return new Plugin<TypewriterState>({
    key: typewriterKey,
    state: {
      init: () => ({
        active: false,
        yielded: false,
        decorations: DecorationSet.empty,
      }),
      apply(tr, value, _old, state) {
        const meta = tr.getMeta(typewriterKey) as TypewriterMeta | undefined;
        let { active, yielded } = value;
        if (meta !== undefined && "active" in meta) {
          active = meta.active;
          yielded = false;
        }
        if (meta !== undefined && "yield" in meta) yielded = true;
        else if (tr.docChanged) yielded = false;
        const changed = tr.docChanged || tr.selectionSet || meta !== undefined;
        return {
          active,
          yielded,
          decorations:
            active === false
              ? DecorationSet.empty
              : changed
                ? dimDecorations(state)
                : value.decorations,
        };
      },
    },
    props: {
      decorations: (state) =>
        typewriterKey.getState(state)?.decorations ?? null,
    },
    view(view) {
      let dragging = false;
      const onDown = (): void => {
        dragging = true;
      };
      const onUp = (): void => {
        if (dragging === false) return;
        dragging = false;
        maybeRecenter(view);
      };
      const maybeRecenter = (current: EditorView): void => {
        const state = typewriterKey.getState(current.state);
        const host = scrollHost();
        if (state === undefined || state.active === false || state.yielded)
          return;
        if (
          dragging ||
          current.state.selection.empty === false ||
          host === null
        )
          return;
        recenter(current, host);
      };
      view.dom.addEventListener("mousedown", onDown);
      window.addEventListener("mouseup", onUp);
      return {
        update(current, prev) {
          if (
            current.state.doc !== prev.doc ||
            current.state.selection !== prev.selection
          ) {
            maybeRecenter(current);
          } else if (
            typewriterKey.getState(current.state)?.active !==
            typewriterKey.getState(prev)?.active
          ) {
            maybeRecenter(current);
          }
        },
        destroy() {
          view.dom.removeEventListener("mousedown", onDown);
          window.removeEventListener("mouseup", onUp);
        },
      };
    },
  });
}

export function setTypewriter(view: EditorView, active: boolean): void {
  view.dispatch(view.state.tr.setMeta(typewriterKey, { active }));
}

/** Call before a nav scroll so typewriter doesn't fight its target. */
export function yieldTypewriter(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(typewriterKey, { yield: true }));
}

export function typewriterActive(state: EditorState): boolean {
  const value = typewriterKey.getState(state);
  return value !== undefined && value.active && value.yielded === false;
}
